import type { AudioService } from "./ports/AudioService";
import type {
  AudioBusId,
  AudioEvent,
  AudioPlayOpts,
  AudioSpeedMode,
  FeatureAudioState,
} from "./ports/types";
import type { AudioBank, GameAudioOverrides } from "./banks/types";
import { DefaultAudioBank, SHELL_BASE_MUSIC_ID } from "./default-pack/defaultBank";
import { mergeAudioMapping } from "./default-pack/defaultMapping";
import { WebAudioEngine } from "./core/WebAudioEngine";
import { AudioBankLoader } from "./banks/AudioBankLoader";
import { preloadCriticalSounds } from "./banks/preloadCriticalSounds";
import type { AudioBundleUrlResolver } from "./banks/AudioBankLoader";
import { AudioMixer } from "./mixer/AudioMixer";
import { MusicDucking } from "./mixer/MusicDucking";
import { AudioLifecycle } from "./lifecycle/AudioLifecycle";
import { AudioLoops } from "./loops/AudioLoops";
import { AudioPlayPolicy } from "./policy/AudioPlayPolicy";

export function createAudioService(params: {
  gameOverrides?: GameAudioOverrides | null;
  dev?: boolean;
  devLog?: (m: string) => void;
  getActionIsSkippable?: () => boolean;
  /** Maps `bundle_audio` locators to URLs (asset manifest). Absent → those sounds are skipped. */
  resolveBundleAudioUrl?: AudioBundleUrlResolver;
}): AudioService {
  // A voice stolen over the cap is counted by the play policy, built below (the callback runs later, on a play).
  const engine = new WebAudioEngine({ maxConcurrentSources: 24, onVoiceStolen: () => playPolicy.countStolenVoice() });
  const loader = new AudioBankLoader(engine, params.dev ? params.devLog : undefined, params.resolveBundleAudioUrl);

  const banks: AudioBank[] = [DefaultAudioBank, ...(params.gameOverrides?.banks ?? [])];
  loader.registerBanks(banks);

  // Apply per-id defaultOpts partial overrides (e.g. gain tweaks) AFTER
  // bank registration. Lets the game adjust individual fields on shell
  // assets without re-declaring the entire entry.
  const defaultOptsOverrides = params.gameOverrides?.defaultOptsOverrides;
  if (defaultOptsOverrides) {
    for (const id of Object.keys(defaultOptsOverrides)) {
      const override = defaultOptsOverrides[id];
      if (override) loader.applyDefaultOptsOverride(id, override);
    }
  }

  // A burst of grouped sounds plays once, at the end of the current moment, as the sound its group names
  // (`AudioPlayOpts.clusterGroup`), past the cluster. Bound late: `playSound` needs the policy it is handed to.
  const playPolicy = new AudioPlayPolicy({
    mapping: mergeAudioMapping(params.gameOverrides?.mapping),
    turboSkipEventIds: params.gameOverrides?.turboSkipEventIds,
    scheduleFlush: (flush) => queueMicrotask(flush),
    playRepresentative: (group) => playSound(group, undefined, true),
  });

  // The mixer graph, built at once: the context starts suspended and is resumed by the first user
  // gesture (`unlockFromUserGesture`), so nothing here needs the gesture to exist.
  const ctx = engine.ensureContext();
  const mixer = new AudioMixer(ctx);
  const duck = new MusicDucking(mixer);
  for (const [k, v] of Object.entries(params.gameOverrides?.buses ?? {})) {
    mixer.setBusEnabled(k as AudioBusId, !!v);
  }
  for (const [k, v] of Object.entries(params.gameOverrides?.busVolumes ?? {})) {
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) mixer.setBusVolume(k as AudioBusId, v);
  }
  /**
   * A one-shot through the policy: a missing asset is counted (and dev-logged); a disabled bus is
   * silent; a coalesced / rate-limited cluster is counted; a missing buffer starts its preload and
   * drops this play. `bypassCluster` is for the representatives a burst's flush releases.
   */
  const playSound = (soundId: string, extra?: AudioPlayOpts, bypassCluster = false) => {
    const asset = loader.getAsset(soundId);
    if (!asset) {
      playPolicy.countMissingSound();
      if (params.dev) params.devLog?.(`[audio] missing soundId: ${soundId}`);
      return;
    }
    const plan = playPolicy.planOneShot({ asset, extra, bypassCluster, isBusEnabled: (bus) => mixer.isBusEnabled(bus) });
    if (!plan) return;
    const buffer = loader.getBuffer(soundId);
    if (!buffer) {
      void loader.preloadOne(soundId);
      return;
    }
    engine.playOneShot({
      id: soundId,
      buffer,
      destination: mixer.getBusInputNode(plan.bus),
      priority: plan.priority,
      protectFromSteal: plan.protectFromSteal,
      stopOnSlam: plan.stopOnSlam,
      playbackRate: plan.playbackRate,
      gain: plan.gain,
    });
  };

  const loops = new AudioLoops({
    ctx,
    engine,
    mixer,
    loader,
    baseMusicId: params.gameOverrides?.baseMusicId ?? SHELL_BASE_MUSIC_ID,
    featureStateMusicMap: params.gameOverrides?.featureStateMusicMap ?? null,
    ambientTrackId: params.gameOverrides?.ambientTrackId ?? null,
    onMissingSound: () => playPolicy.countMissingSound(),
    devLog: params.dev ? params.devLog : undefined,
  });

  const lifecycle = new AudioLifecycle({
    engine,
    reactions: {
      // `pagehide`: the beds, the intro and the big-win count stop. The keyed loops (the reel roll)
      // follow their owner instead: a page restored mid-spin resumes the spin, and its roll with it.
      teardown: () => {
        loops.cancelPendingIntro();
        loops.stopBigWinCountLoop();
        loops.stopBedsImmediate();
      },
      resume: async (fresh) => {
        // Clear the (invalidated or already-stopped) current loops so the restart below
        // bypasses `crossfadeTo`'s same-id short-circuit and creates a fresh source.
        if (fresh) loops.stopBedsImmediate();
        await engine.resumeIfSuspended();
        // The music and the ambient bed last asked for, not the feature-state or configured bed (review
        // finding 11, 2026-09-27 #55); a start refused here is kept for the re-unlock (2026-09-27 #25).
        loops.restoreMusic(fresh);
        loops.restoreAmbient();
      },
      reactivated: () => {
        loops.restoreMusic(false);
        loops.restoreAmbient();
      },
    },
  });

  /**
   * What the first successful unlock starts, once: the lifecycle listeners and the beds. The
   * downloads began with `preloadSounds`, at the end of the boot; this joins them (or starts them,
   * when nothing called it). Each bed starts the moment its own file is in: until 2026-09-26 both
   * waited for the whole set — about 8 MB with the ambient bed, tens of seconds on a slow link —
   * and none of it was fetched before the tap.
   */
  let started = false;
  const startAfterUnlock = (): void => {
    if (started) return;
    started = true;
    lifecycle.bind();
    void preloadCriticalSounds(loader, params.gameOverrides);
    void loops.startBedsWhenLoaded();
  };

  return {
    control: {
      preloadSounds() {
        void preloadCriticalSounds(loader, params.gameOverrides);
      },

      unlockFromUserGesture() {
        // Armed first: when this call has no gesture behind it, the browser may refuse it or leave
        // the resume pending, and the player's next tap anywhere is what unlocks.
        lifecycle.armGestureUnlock(() => startAfterUnlock());
        void lifecycle.unlockFromUserGesture().then((unlocked) => {
          if (unlocked) startAfterUnlock();
        });
      },

      setSpeedMode(mode: AudioSpeedMode) {
        playPolicy.setSpeedMode(mode);
      },

      setFeatureState(state: FeatureAudioState) {
        loops.setFeatureState(state);
        loops.playBaseMusic();
      },

      setMusicTrack(id: string, fadeMs?: number) {
        loops.playMusic(id, typeof fadeMs === "number" ? fadeMs : 300);
      },

      playMusicWithIntro(introId: string, loopId: string) {
        loops.playMusicWithIntro(introId, loopId);
      },

      setBusEnabled(bus: AudioBusId, enabled: boolean) {
        mixer.setBusEnabled(bus, enabled);
      },

      setMasterVolume(volume: number) {
        mixer.setMasterVolume(volume);
      },

      setAmbientTrack(id: string | null, fadeMs?: number) {
        loops.setAmbient(id, typeof fadeMs === "number" ? fadeMs : 300);
      },

      notifySlam() {
        engine.notifySlamStop();
      },

      duckMusic(amount?: number, fadeMs?: number) {
        duck.duck(amount, fadeMs);
      },

      unduckMusic(fadeMs?: number) {
        duck.unduck(fadeMs);
      },

      __getDebugSnapshot() {
        return {
          ctxState: engine.audioContext?.state ?? "none",
          unlocked: engine.isUnlocked(),
          activeSources: engine.getActiveSourcesCount(),
          currentLoopId: loops.currentMusicId,
          currentAmbientLoopId: loops.currentAmbientId,
          ducking: duck.isActive(),
          buses: {
            music: mixer.isBusEnabled("music"),
            sfx: mixer.isBusEnabled("sfx"),
            ui: mixer.isBusEnabled("ui"),
            voice: mixer.isBusEnabled("voice"),
            ambient: mixer.isBusEnabled("ambient"),
          },
          drops: { ...playPolicy.drops },
        };
      },
    },

    presentation: {
      emit(event: AudioEvent) {
        lifecycle.reactivateIfNeededFromGesture();

        const soundId = playPolicy.resolveSoundId(event);
        if (!soundId) {
          return;
        }

        const asset = loader.getAsset(soundId);
        
        const essential = asset?.defaultOpts?.essential ?? false;
        const actionIsSkippableInSuper = !!params.getActionIsSkippable?.();

        if (playPolicy.decideEvent({ event, essential, actionIsSkippableInSuper }).kind === "skip") {
          return;
        }

        if (event.id === "bigwin.start") {
          duck.duck(0.35, 120);
        }

        if (event.id === "bigwin.count_start") {
          loops.startBigWinCountLoop(soundId);
          return;
        }

        if (event.id === "bigwin.count_end") {
          // Music returns to full volume the moment the number scroll
          // finishes — the dramatic ducked window is just for the count-up,
          // not the entire panel-dismissal wait.
          loops.stopBigWinCountLoop();
          duck.unduck(160);
          playSound(soundId);
          return;
        }

        if (event.id === "bigwin.end") {
          loops.stopBigWinCountLoop();
          // Safety net: redundant unduck in case count_end never fired (error
          // path between count_start and count_end). Idempotent — setBusDuck
          // to 1 when already at 1 is harmless.
          duck.unduck(160);
        }

        playSound(soundId);
      },

      playSoundId(soundId: string, opts?: AudioPlayOpts) {
        lifecycle.reactivateIfNeededFromGesture();
        playSound(soundId, opts);
      },

      startSfxLoop(soundId: string) {
        lifecycle.reactivateIfNeededFromGesture();
        loops.startSfxLoop(soundId);
      },

      stopSfxLoop(soundId: string) {
        loops.stopSfxLoop(soundId);
      },

      setSfxLoopGainScale(soundId: string, scale: number) {
        loops.setSfxLoopGainScale(soundId, scale);
      },
    },
  };
}